// src/pages/outsourcing_management/InquiryPriceFields.tsx
//
// The prices a purchase order carries (EE-253): one per line, plus transport,
// all in kroner excl. VAT as the buyer types them and øre as the backend keeps
// them. Part of the convert form, in both of its modes - what we order at, and
// what the supplier confirmed.
//
// Every price is optional: an order can go out before a price is agreed, and
// a line left empty is simply not printed with one. What cannot happen here is
// clearing a price that is already saved - the backend only ever sets the
// prices it is sent - so an emptied saved price is flagged rather than
// silently kept.
import { FunctionComponent, useMemo, useState } from "react";
import {
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { InquiryDetailType } from "types/InquiryTypes";
import { InquiryItemPriceType } from "util/network/Inquiries";
import {
  centsToInput,
  formatCentsOrDash,
  parseKrToCents,
  ParsedCents,
} from "util/money";
import { showsQuantity } from "components/Inquiry/items/inquiryItemColumns";

export type PriceValues = {
  /** The lines with a price typed in - a line left empty is not sent. */
  itemPrices: InquiryItemPriceType[];
  transportCostCents?: number;
  /** False while any field holds something that is not an amount. */
  pricesValid: boolean;
};

const CLEARED =
  "A saved price cannot be cleared here - enter 0 to show it as free.";

/** A parse, with the "was saved, now empty" case turned into an error. */
const check = (text: string, saved: number | null | undefined): ParsedCents => {
  const parsed = parseKrToCents(text);
  if (parsed.ok && parsed.cents === null && saved != null) {
    return { ok: false, error: CLEARED };
  }
  return parsed;
};

/**
 * The typed text per line and for transport, and what it adds up to.
 * Seeded from what the inquiry already has, so confirming an order opens on
 * the prices it was sent with.
 */
export const usePriceDraft = (inquiry: InquiryDetailType) => {
  const [lineText, setLineText] = useState<Record<number, string>>(() =>
    Object.fromEntries(
      inquiry.items.map((item) => [item.id, centsToInput(item.priceCents)])
    )
  );
  const [transportText, setTransportText] = useState(
    centsToInput(inquiry.transportCostCents)
  );

  const parsed = useMemo(() => {
    const lines = inquiry.items.map((item) => ({
      item,
      result: check(lineText[item.id] ?? "", item.priceCents),
    }));
    const transport = check(transportText, inquiry.transportCostCents);
    const itemPrices: InquiryItemPriceType[] = [];
    lines.forEach(({ item, result }) => {
      if (result.ok && result.cents !== null) {
        itemPrices.push({ itemId: item.id, priceCents: result.cents });
      }
    });
    const transportCostCents =
      transport.ok && transport.cents !== null ? transport.cents : undefined;
    // Summed the way the backend sums them: the priced lines only, and
    // nothing at all while no line and no transport is priced.
    const itemsTotal = itemPrices.length
      ? itemPrices.reduce((sum, line) => sum + line.priceCents, 0)
      : null;
    const total =
      itemsTotal === null && transportCostCents === undefined
        ? null
        : (itemsTotal ?? 0) + (transportCostCents ?? 0);
    const values: PriceValues = {
      itemPrices,
      transportCostCents,
      pricesValid: transport.ok && lines.every(({ result }) => result.ok),
    };
    return { lines, transport, itemsTotal, total, values };
  }, [inquiry, lineText, transportText]);

  return {
    ...parsed,
    lineText,
    setLine: (itemId: number, text: string) =>
      setLineText((current) => ({ ...current, [itemId]: text })),
    transportText,
    setTransportText,
  };
};

type PropsType = {
  inquiry: InquiryDetailType;
  draft: ReturnType<typeof usePriceDraft>;
};

const KR = <InputAdornment position="end">kr.</InputAdornment>;

const PRICE_INPUT_SX = { width: "100%", maxWidth: 180 };

const lineName = (item: InquiryDetailType["items"][number]) =>
  item.product?.name || item.comment || `Line #${item.id}`;

const InquiryPriceFields: FunctionComponent<PropsType> = ({
  inquiry,
  draft,
}) => {
  const showQty = showsQuantity(inquiry.type);
  return (
    <>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        Prices excl. VAT
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block">
        Optional - a line total per line, printed on the purchase order.
      </Typography>
      <Table size="small" sx={{ tableLayout: "fixed", mt: 0.5 }}>
        <TableHead>
          <TableRow>
            <TableCell>Line</TableCell>
            {showQty && (
              <TableCell align="right" sx={{ width: 64 }}>
                Qty
              </TableCell>
            )}
            <TableCell sx={{ width: "45%" }}>Line total</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {draft.lines.map(({ item, result }) => (
            <TableRow key={item.id}>
              <TableCell
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={lineName(item)}
              >
                {lineName(item)}
              </TableCell>
              {showQty && (
                <TableCell align="right">{item.quantity ?? "-"}</TableCell>
              )}
              <TableCell>
                <TextField
                  size="small"
                  sx={PRICE_INPUT_SX}
                  inputProps={{
                    inputMode: "decimal",
                    "aria-label": `Price for ${lineName(item)}`,
                  }}
                  InputProps={{ endAdornment: KR }}
                  value={draft.lineText[item.id] ?? ""}
                  onChange={(e) => draft.setLine(item.id, e.target.value)}
                  error={!result.ok}
                  helperText={result.ok ? undefined : result.error}
                />
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={showQty ? 2 : 1}>Transport</TableCell>
            <TableCell>
              <TextField
                size="small"
                sx={PRICE_INPUT_SX}
                inputProps={{ inputMode: "decimal", "aria-label": "Transport" }}
                InputProps={{ endAdornment: KR }}
                value={draft.transportText}
                onChange={(e) => draft.setTransportText(e.target.value)}
                error={!draft.transport.ok}
                helperText={
                  draft.transport.ok ? undefined : draft.transport.error
                }
              />
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={showQty ? 2 : 1}>Items total</TableCell>
            <TableCell data-testid="price-items-total">
              {formatCentsOrDash(draft.itemsTotal)}
            </TableCell>
          </TableRow>
          <TableRow>
            <TableCell colSpan={showQty ? 2 : 1} sx={{ fontWeight: "bold" }}>
              Total excl. VAT
            </TableCell>
            <TableCell sx={{ fontWeight: "bold" }} data-testid="price-total">
              {formatCentsOrDash(draft.total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </>
  );
};

export default InquiryPriceFields;
